import { Injectable, computed, inject, signal } from '@angular/core';
import {
  BrandApiService,
  MergedSkinResponse,
  SkinAsset,
  SkinDto,
} from './brand-api.service';
import { ToggleThemeService } from './toggle-theme.service';
/* eslint-disable */

/** Reserved keys in the skin's `assets` map. */
export const LOGO_ASSET_KEY = 'header_logo';
export const FAVICON_ASSET_KEY = 'favicon';

/**
 * A customer "brand" / skin. These values come from the Customer Skinning API
 * (keyed per customer); nothing about the skin is persisted in the browser.
 */
export interface Brand {
  name: string;
  appTitle: string; // browser tab title
  font: string; // CSS font-family stack for the whole platform
  radius: number; // corner radius in px (cards, buttons)
  typeScale: number; // whole-UI scale, percent (100 = default)
  colors: {
    primary: string; // dominant brand color (buttons, headings)
    secondary: string; // accent color (icons, highlights)
    link: string; // links / interactive text
    sidenav: string; // left navigation rail (often darker than primary)
    splash: string; // splash / loading screen background
    background: string; // app canvas / page background
    surface: string; // cards, panels, inputs, navbar
    text: string; // body copy
    // --- optional per-brand DARK canvas ---
    // When set, these override the app's standard dark theme while the brand is
    // active in dark mode. When omitted, the app's default dark canvas is used.
    darkBackground?: string; // dark app canvas / page background
    darkSurface?: string; // dark cards, panels, inputs, navbar
    darkText?: string; // dark body / strong text
  };
  logoUrl: string; // resolved logo URL (presigned from S3, or the default asset)
  faviconUrl: string; // resolved favicon URL
  /** Reserved assets map as stored by the API ({ header_logo: {...}, favicon: {...} }). */
  assets?: Record<string, SkinAsset>;
}

/**
 * Brand tokens are the small, curated set of things a customer controls.
 * Each token fans out to the many CSS custom properties defined in
 * `variables.scss`, so one picker changes the whole platform coherently.
 *
 * This is deliberately a SUBSET of the ~168 variables — the rest keep their
 * defaults. Curated tokens keep contrast/readability under control.
 */
/**
 * ACCENT tokens carry a raw brand color (fills, buttons, nav, links) that reads
 * fine on either a light or a dark canvas — so they're applied in BOTH modes.
 * Keyed by the `Brand['colors']` field that feeds them.
 */
const BRAND_ACCENT_TOKENS: Partial<Record<keyof Brand['colors'], string[]>> = {
  primary: [
    '--navy',
    '--spinner',
    '--navy-bg',
    '--dark-bg',
    '--loader',
    '--progress',
    '--primary-btn',
    '--navy-card',
    '--rdbtn-thumb',
  ],
  secondary: ['--orange', '--secondary-btn', '--card-icon'],
  link: ['--blue-text', '--link-text', '--inventory-value'],
  sidenav: ['--side-nav'],
  splash: ['--splash'],
};

/**
 * NEUTRAL tokens are the readability-sensitive canvas / surface / text vars.
 * They're applied only in LIGHT mode; in DARK mode the `body.dark-theme`
 * stylesheet owns them, so text stays legible — this is what gives each brand
 * a proper dark variant instead of forcing a reset. Keyed by the brand color
 * that feeds them (`primary` here means the brand's text-colored vars).
 */
const BRAND_NEUTRAL_TOKENS: Partial<Record<keyof Brand['colors'], string[]>> = {
  primary: ['--dark-text', '--navy-text', '--active-link-text', '--link-blue', '--heading-text'],
  background: ['--bg-color', '--even-table-row', '--border', '--inset-bg'],
  surface: [
    '--white-bg',
    '--card-bg',
    '--navbar',
    '--chart-card',
    '--odd-table-row',
    '--input-bg',
    '--view-toggle-btn',
  ],
  text: [
    '--grey-text',
    '--table-cell-text',
    '--modal-text',
    '--label',
    '--table-pagination-text',
    '--inventory-main-title',
    '--grayscale-text',
    '--black-text',
  ],
};

/**
 * Button / interactive-accent variables that aren't a raw brand color but a
 * light TINT of the primary (e.g. the pale accent-button fill, hover states).
 * They are derived from `primary` at runtime with `color-mix`, so every button
 * follows the active theme instead of staying a hardcoded blue tint.
 * Each entry is [cssVar, mix% of primary over white].
 */
const PRIMARY_TINT_TOKENS: Array<[string, number]> = [
  ['--accent-btn', 14],
  ['--light-btn', 12],
  ['--action-btn', 8],
  ['--outline-btn', 45],
  ['--active-link', 40],
  ['--table-row-hover', 22],
  ['--expansion-panel-header', 14],
  ['--metrics-card', 10],
];

/** Button text/border accents that take the solid primary color. */
const PRIMARY_SOLID_TOKENS = ['--accent-btn-text', '--light-btn-text', '--light-mode-only-border'];

/** The out-of-the-box NetsyncIO look — used as the reset target. */
export const DEFAULT_BRAND: Brand = {
  name: 'NetsyncIO (default)',
  appTitle: 'Netsync IO',
  font: '"Roboto", sans-serif',
  radius: 8,
  typeScale: 100,
  colors: {
    primary: '#014b76',
    secondary: '#f6921e',
    link: '#0584ce',
    sidenav: '#014b76',
    splash: '#014b76',
    background: '#f5f5f5',
    surface: '#ffffff',
    text: '#606060',
  },
  logoUrl: '/assets/icons/logo.svg',
  faviconUrl: 'favicon.ico',
};

/** A few ready-made skins so the demo lands in one click. */
export const BRAND_PRESETS: Brand[] = [
  DEFAULT_BRAND,
  {
    name: 'Cobalt Cloud',
    appTitle: 'Cobalt Cloud',
    font: '"Poppins", sans-serif',
    radius: 16,
    typeScale: 100,
    colors: {
      primary: '#2f6bff',
      secondary: '#1b3a8f',
      link: '#2f6bff',
      sidenav: '#0b1f4d',
      splash: '#2f6bff',
      background: '#f4f7ff',
      surface: '#ffffff',
      text: '#1a2340',
    },
    logoUrl: '/assets/icons/logo.svg',
    faviconUrl: 'favicon.ico',
  },
  {
    name: 'Azure Gold',
    appTitle: 'Azure Gold',
    font: '"Montserrat", "Segoe UI", sans-serif',
    radius: 6,
    typeScale: 100,
    colors: {
      primary: '#003087',
      secondary: '#f2a900',
      link: '#0057b8',
      sidenav: '#002766',
      splash: '#003087',
      background: '#eef3f9',
      surface: '#ffffff',
      text: '#1c2b45',
    },
    logoUrl: '/assets/icons/logo.svg',
    faviconUrl: 'favicon.ico',
  },
  {
    name: 'Emerald Corp',
    appTitle: 'Emerald Cloud Console',
    font: '"Poppins", sans-serif',
    radius: 4,
    typeScale: 100,
    colors: {
      primary: '#0f6b4f',
      secondary: '#f2a900',
      link: '#0f9d76',
      sidenav: '#0a3d2e',
      splash: '#0f6b4f',
      background: '#f0f5f2',
      surface: '#ffffff',
      text: '#33413b',
    },
    logoUrl: '/assets/icons/logo.svg',
    faviconUrl: 'favicon.ico',
  },
  {
    name: 'Crimson Cloud',
    appTitle: 'Crimson Cloud',
    font: 'Georgia, "Times New Roman", serif',
    radius: 2,
    typeScale: 100,
    colors: {
      primary: '#7a1f2b',
      secondary: '#e06c2b',
      link: '#c0392b',
      sidenav: '#4a121a',
      splash: '#7a1f2b',
      background: '#f7f0f0',
      surface: '#ffffff',
      text: '#4a3b3b',
    },
    logoUrl: '/assets/icons/logo.svg',
    faviconUrl: 'favicon.ico',
  },
  {
    name: 'Violet Systems',
    appTitle: 'Violet Systems Portal',
    font: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    radius: 14,
    typeScale: 105,
    colors: {
      primary: '#4a2b7a',
      secondary: '#e0a32b',
      link: '#7c4dff',
      sidenav: '#2c1a4a',
      splash: '#4a2b7a',
      background: '#f4f1f9',
      surface: '#ffffff',
      text: '#3d3550',
    },
    logoUrl: '/assets/icons/logo.svg',
    faviconUrl: 'favicon.ico',
  },
];

@Injectable({ providedIn: 'root' })
export class BrandService {
  /** Legacy localStorage keys from earlier skin implementations. The skin
   *  feature is now fully server-backed; these are purged once on boot so no
   *  skin data lingers in the browser. */
  private readonly LEGACY_KEYS = [
    'CUSTOMER_BRAND',
    'BRAND_TEMPLATES',
    'BRAND_TEMPLATES_CUSTOM',
    'BRAND_TEMPLATE_ACTIVE',
    'BRAND_ASSET_HISTORY_logo',
    'BRAND_ASSET_HISTORY_favicon',
  ];

  private readonly api = inject(BrandApiService);
  private readonly darkMode = inject(ToggleThemeService);

  /** Bound in templates (e.g. the header logo) so uploads apply live. */
  readonly logoUrl = signal<string>(DEFAULT_BRAND.logoUrl);

  /** The brand currently applied — drives the drawer's form state. */
  readonly current = signal<Brand>(DEFAULT_BRAND);

  /**
   * True when a non-default brand is live. Used to gate dark mode: a custom
   * brand sets background/surface/text inline (overriding the dark-theme class),
   * so dark mode is only allowed once the brand is reset to the default.
   */
  readonly isCustomBrandActive = computed(
    () => this.current().name !== DEFAULT_BRAND.name,
  );

  /**
   * Call once at app start, after the user is authenticated. Fetches the active
   * skin from the API (GET /skin/user = corporate skin merged with this user's
   * overrides) and applies it. Nothing about the skin is persisted locally.
   */
  init(): void {
    this.purgeLegacyStorage();

    this.api.getMergedSkin().subscribe({
      next: (resp) => {
        const brand = this.brandFromMerged(resp);
        if (brand) this.apply(brand);
      },
      // 404 = no active skin for this customer/user yet → keep the default look.
      error: () => {
        /* keep the default look */
      },
    });
  }

  /** Remove any skin data left in localStorage by earlier implementations. */
  private purgeLegacyStorage(): void {
    try {
      this.LEGACY_KEYS.forEach((key) => localStorage.removeItem(key));
    } catch {
      /* best-effort */
    }
  }

  /**
   * Resolve GET /skin/user into a Brand. A custom active skin arrives fully
   * merged under `skin`; a predefined one arrives as a pointer + raw overrides,
   * so we resolve the look from the frontend presets and layer the overrides on.
   */
  private brandFromMerged(resp: MergedSkinResponse): Brand | null {
    if (resp.skin_type === 'custom' && resp.skin) {
      return this.fromDto(resp.skin);
    }
    if (resp.skin_type === 'predefined') {
      const preset = BRAND_PRESETS.find((p) => p.name === resp.skin_name);
      if (!preset) return null;
      const o = resp.overrides ?? {};
      return {
        ...structuredClone(preset),
        font: o.font ?? preset.font,
        typeScale: o.typeScale ?? preset.typeScale,
      };
    }
    return null;
  }

  // ---------- API <-> Brand mapping ----------

  /** Build a Brand from the API's skin document. */
  private fromDto(dto: SkinDto): Brand {
    const assets = dto.assets ?? {};
    return {
      name: dto.skin_name ?? DEFAULT_BRAND.name,
      appTitle: dto.appTitle ?? DEFAULT_BRAND.appTitle,
      font: dto.font ?? DEFAULT_BRAND.font,
      radius: dto.radius ?? DEFAULT_BRAND.radius,
      typeScale: dto.typeScale ?? DEFAULT_BRAND.typeScale,
      colors: { ...DEFAULT_BRAND.colors, ...(dto.colors ?? {}) },
      logoUrl: assets[LOGO_ASSET_KEY]?.url || DEFAULT_BRAND.logoUrl,
      faviconUrl: assets[FAVICON_ASSET_KEY]?.url || DEFAULT_BRAND.faviconUrl,
      assets,
    };
  }

  /** The corporate half of the skin (POST /skin). Presigned urls are stripped. */
  private toCustomerSkinDto(brand: Brand): SkinDto {
    const assets: Record<string, SkinAsset> = {};
    Object.entries(brand.assets ?? {}).forEach(([key, a]) => {
      if (a?.filename) assets[key] = { type: a.type, filename: a.filename };
    });
    return {
      skin_name: brand.name,
      appTitle: brand.appTitle,
      radius: brand.radius,
      colors: brand.colors,
      assets,
    };
  }

  // ---------- persistence ----------

  /**
   * Corporate brand (colors, logo, shape) — applies to every user of the
   * customer. Admin-only; the server enforces it.
   */
  saveCorporate(brand: Brand): Promise<void> {
    this.apply(brand);
    return new Promise((resolve, reject) =>
      this.api.saveCustomerSkin(this.toCustomerSkinDto(brand)).subscribe({
        next: () => resolve(),
        error: reject,
      }),
    );
  }

  /** Personal overrides (font, type scale) — this user only. */
  savePersonal(brand: Brand): Promise<void> {
    this.apply(brand);
    return new Promise((resolve, reject) =>
      this.api
        .saveUserOverrides({ font: brand.font, typeScale: brand.typeScale })
        .subscribe({ next: () => resolve(), error: reject }),
    );
  }

  /** Re-apply the current brand — call after the light/dark theme toggles so the
   *  brand recomputes its canvas for the new mode. */
  reapply(): void {
    this.apply(this.current());
  }

  /** Apply a brand to the running app: CSS vars, logo, splash, tab title/icon.
   *  Dark-mode aware — see the ACCENT vs NEUTRAL token split above. */
  apply(brand: Brand): void {
    // Applying the default brand just clears the overrides so the stock light or
    // dark theme shows through cleanly.
    if (brand.name === DEFAULT_BRAND.name) {
      this.reset();
      return;
    }

    // NOTE: target <body>, not <html>. variables.scss declares these vars on
    // `body { ... }` (and `body.dark-theme`), so an inline style on the body is
    // the only override with higher specificity than those stylesheet rules.
    const root = document.body;
    const dark = this.darkMode.isDarkThemeSelected;

    // Brand accents (buttons, nav, links) apply in both light and dark.
    this.setGroups(root, BRAND_ACCENT_TOKENS, brand);

    if (dark) {
      // Dark variant: pale button tints are owned by the dark theme.
      this.clearTints(root);
      // Each dark-canvas field, when the brand defines one, overrides the app's
      // dark theme; otherwise that group falls back to the default dark canvas.
      const c = brand.colors;
      this.applyOrClear(root, BRAND_NEUTRAL_TOKENS.background, c.darkBackground);
      this.applyOrClear(root, BRAND_NEUTRAL_TOKENS.surface, c.darkSurface);
      this.applyOrClear(root, BRAND_NEUTRAL_TOKENS.text, c.darkText);
      this.applyOrClear(root, BRAND_NEUTRAL_TOKENS.primary, c.darkText);
    } else {
      this.setGroups(root, BRAND_NEUTRAL_TOKENS, brand);
      const primaryColor = brand.colors.primary;
      if (primaryColor) {
        PRIMARY_TINT_TOKENS.forEach(([cssVar, pct]) =>
          root.style.setProperty(cssVar, `color-mix(in srgb, ${primaryColor} ${pct}%, #fff)`),
        );
        PRIMARY_SOLID_TOKENS.forEach((cssVar) =>
          root.style.setProperty(cssVar, primaryColor),
        );
      }
    }

    root.style.setProperty('--brand-font', brand.font || DEFAULT_BRAND.font);
    root.style.setProperty('--brand-radius', `${brand.radius ?? DEFAULT_BRAND.radius}px`);
    // Type scale multiplies the root font size, so it must live on <html>.
    document.documentElement.style.setProperty(
      '--brand-scale',
      `${(brand.typeScale ?? DEFAULT_BRAND.typeScale) / 100}`,
    );

    this.logoUrl.set(brand.logoUrl || DEFAULT_BRAND.logoUrl);

    // Put the customer logo on the splash screen too. Only when a real brand
    // logo exists — otherwise the baked-in default wordmark stays.
    if (brand.logoUrl && brand.logoUrl !== DEFAULT_BRAND.logoUrl) {
      root.style.setProperty('--splash-logo', `url("${brand.logoUrl}")`);
    } else {
      root.style.removeProperty('--splash-logo');
    }

    document.title = brand.appTitle || DEFAULT_BRAND.appTitle;
    this.setFavicon(brand.faviconUrl || DEFAULT_BRAND.faviconUrl);

    this.current.set(brand);
  }

  /** Set every CSS var in a token group to its brand color. */
  private setGroups(
    root: HTMLElement,
    groups: Partial<Record<keyof Brand['colors'], string[]>>,
    brand: Brand,
  ): void {
    (Object.keys(groups) as (keyof Brand['colors'])[]).forEach((key) => {
      const value = brand.colors[key];
      if (!value) return; // older skins may lack newer tokens — keep defaults
      groups[key]!.forEach((cssVar) => root.style.setProperty(cssVar, value));
    });
  }

  /** Set each var to `value`, or remove it (restoring the stylesheet value)
   *  when `value` is empty. */
  private applyOrClear(root: HTMLElement, vars: string[] | undefined, value?: string): void {
    if (!vars) return;
    vars.forEach((cssVar) =>
      value
        ? root.style.setProperty(cssVar, value)
        : root.style.removeProperty(cssVar),
    );
  }

  /** Remove every CSS var in a set of token groups (restores stylesheet value). */
  private clearGroups(
    root: HTMLElement,
    groups: Partial<Record<keyof Brand['colors'], string[]>>,
  ): void {
    Object.values(groups)
      .flat()
      .forEach((cssVar) => root.style.removeProperty(cssVar));
  }

  /** Remove the derived button tint / solid vars. */
  private clearTints(root: HTMLElement): void {
    [...PRIMARY_TINT_TOKENS.map(([v]) => v), ...PRIMARY_SOLID_TOKENS].forEach(
      (cssVar) => root.style.removeProperty(cssVar),
    );
  }

  /** Restore the default NetsyncIO look in the running app (does not delete the
   *  saved skin on the server). */
  reset(): void {
    const root = document.body;
    this.clearGroups(root, BRAND_ACCENT_TOKENS);
    this.clearGroups(root, BRAND_NEUTRAL_TOKENS);
    this.clearTints(root);
    root.style.removeProperty('--brand-font');
    root.style.removeProperty('--brand-radius');
    root.style.removeProperty('--splash-logo');
    document.documentElement.style.removeProperty('--brand-scale');
    document.title = DEFAULT_BRAND.appTitle;
    this.setFavicon(DEFAULT_BRAND.faviconUrl);
    this.logoUrl.set(DEFAULT_BRAND.logoUrl);
    this.current.set(DEFAULT_BRAND);
  }

  /** The current skin as pretty JSON — the exact POST /skin payload. */
  exportJson(): string {
    return JSON.stringify(this.toCustomerSkinDto(this.current()), null, 2);
  }

  /** Upload a logo/favicon to S3 (presign + PUT) and record it in the assets map. */
  async uploadAsset(
    assetKey: string,
    assetType: string,
    file: File,
    assets: Record<string, SkinAsset>,
  ): Promise<Record<string, SkinAsset>> {
    const asset = await this.api.uploadAsset(assetType, file);
    return { ...assets, [assetKey]: asset };
  }

  private setFavicon(href: string): void {
    let link = document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }
}
